import { ChevronsLeftRight } from 'lucide-react';
import { useEffect, useRef } from 'react';
import type { KeyboardEvent, PointerEvent, RefObject } from 'react';
import { clamp } from '@/motion/math';
import { compareFromKey } from './compare';

export interface CompareDividerProps {
  /** The stage the divider travels across (its width is the track). */
  container: RefObject<HTMLElement | null>;
  /** 0 – 100: how much of the stage, from the left, shows the stone. */
  value: number;
  /** `commit` is true when the change is final (key press, pointer released). */
  onChange: (value: number, commit: boolean) => void;
  label: string;
  /** Spoken value: "60 % stone, 40 % plain surface". */
  valueText: string;
  onDraggingChange?: (dragging: boolean) => void;
}

/**
 * The before/after handle (WAI-ARIA slider pattern). A 64 px wide strip that spans the stage's full
 * height is the hit target, so it can be grabbed anywhere along the line; the visible grip sits in
 * its middle. ← ↓ / → ↑ move 5 %, PageDown / PageUp 10 %, Home / End jump to the ends. Dragging is
 * throttled to one update per frame and `touch-action: pan-y` keeps vertical page scrolling alive.
 */
export function CompareDivider({
  container,
  value,
  onChange,
  label,
  valueText,
  onDraggingChange,
}: CompareDividerProps) {
  const drag = useRef<{ pointerId: number; offset: number; left: number; width: number } | null>(
    null,
  );
  const pending = useRef<number | null>(null);
  const frame = useRef(0);

  useEffect(
    () => () => {
      if (frame.current !== 0) window.cancelAnimationFrame(frame.current);
    },
    [],
  );

  const toValue = (clientX: number, state: { left: number; width: number }): number =>
    state.width > 0 ? ((clientX - state.left) / state.width) * 100 : 0;

  const flush = (commit: boolean): void => {
    if (frame.current !== 0) {
      window.cancelAnimationFrame(frame.current);
      frame.current = 0;
    }
    if (pending.current !== null) {
      onChange(pending.current, commit);
      if (commit) pending.current = null;
    }
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    const rect = container.current?.getBoundingClientRect();
    if (!rect) return;
    const state = { left: rect.left, width: rect.width };
    drag.current = {
      pointerId: event.pointerId,
      left: state.left,
      width: state.width,
      offset: value - toValue(event.clientX, state),
    };
    // (absent in some test environments; a real browser always has it)
    event.currentTarget.setPointerCapture?.(event.pointerId);
    onDraggingChange?.(true);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    pending.current = clamp(toValue(event.clientX, state) + state.offset, 0, 100);
    if (frame.current === 0) {
      frame.current = window.requestAnimationFrame(() => {
        frame.current = 0;
        if (pending.current !== null) onChange(pending.current, false);
      });
    }
  };

  const onPointerEnd = (event: PointerEvent<HTMLDivElement>): void => {
    const state = drag.current;
    if (!state || state.pointerId !== event.pointerId) return;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    onDraggingChange?.(false);
    if (pending.current === null) pending.current = value;
    flush(true);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const next = compareFromKey(event.key, value);
    if (next === null) return;
    event.preventDefault();
    onChange(next, false);
  };

  return (
    // role="slider" is an interactive widget: keyboard + pointer handlers belong here
    <div
      className="viz-divider"
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-orientation="horizontal"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value)}
      aria-valuetext={valueText}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onKeyDown={onKeyDown}
    >
      <span className="viz-divider__grip" aria-hidden="true">
        <ChevronsLeftRight size={20} />
      </span>
    </div>
  );
}
